import React, { useEffect, useMemo, useState } from 'react'
import { addDays, startOfDay } from '@e3dc-react/shell/Libs/date'
import { useParams, useNavigate } from 'react-router-dom'
import { useAI360ChartData } from '../hooks'
import DetailsPage from './DetailsPage'
import { Stack, useMediaQuery, useTheme } from '@mui/material'
import { makeStyles } from 'tss-react/mui'
import { useUserSettings } from 'src/Hooks'
import { LineChartStore } from '../store'

const useStyles = makeStyles()(theme => ({
  container: {
    height: '100%',
    backgroundColor: theme.palette.common.white,
    overflow: 'hidden',
    gap: theme.spacing(1),
    [theme.breakpoints.up('md')]: {
      border: `1px solid ${theme.palette.shades.shade03}`,
      borderRadius: theme.shape.borderRadius,
      padding: theme.spacing(4),
      maxHeight: '600px',
    },
  },
}))

/**
 * component to provide DetailPage with necessary data.
 */
const DetailsPageContainer: React.FC = () => {
  const { installationId, storageId } = useParams<{ installationId: string; storageId: string }>()
  const [dateSelected, setDateSelected] = useState<Date>(startOfDay(new Date()))
  const [isModeModalOpen, setIsModeModalOpen] = useState<boolean>(false)
  const { classes } = useStyles()
  const navigate = useNavigate()
  const theme = useTheme()
  const upMd = useMediaQuery(theme.breakpoints.up('md'))

  /* AI360 uses time frame of 1 day: 00:00 to 00:00 next day */
  const from = useMemo(() => startOfDay(dateSelected), [dateSelected])
  const to = useMemo(() => startOfDay(addDays(dateSelected, 1)), [dateSelected])

  const chartData = useAI360ChartData({
    installationId,
    storageId,
    from,
    to,
  })

  const {
    userSettings: { isAI360DetailsChartLegendEnabled },
  } = useUserSettings()

  useEffect(() => {
    /* navigate to home page in desktop view. It prevents accessing this page by entering url manually in desktop view */
    if (upMd) {
      navigate(`/dashboard/ai360/${installationId}/${storageId}`, { replace: true })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [upMd])

  /* update AI360 detail chart legends state from user settings in the LineChartStore */
  useEffect(() => {
    if (!isAI360DetailsChartLegendEnabled) return

    LineChartStore.set({
      isLegendEnabled: isAI360DetailsChartLegendEnabled,
    })
  }, [isAI360DetailsChartLegendEnabled])

  return (
    <Stack className={classes.container}>
      <DetailsPage
        chartData={chartData}
        dateSelected={dateSelected}
        from={from}
        to={to}
        setDateSelected={setDateSelected}
        isModeModalOpen={isModeModalOpen}
        setIsModeModalOpen={setIsModeModalOpen}
      />
    </Stack>
  )
}

export default DetailsPageContainer
